import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { getPublicationStatus } from 'lib/api/documentPublication';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Disclaimer from 'lib/designSystem/Disclaimer';
import { DocumentPublication } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import InvoiceForm from './components/InvoiceForm';
import SuccessDialog from './components/SuccessDialog';
import UnpaidContractForm from './components/UnpaidContractForm';

interface PublicationFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId?: string;
}

type PublicationType = 'invoice' | 'unpaidContract';

export type FormProps = {
  documentId?: string;
  setLoading: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  handleSuccessClose: () => void;
  setIdentifier: (identifier: string) => void;
};

const formRegistry: Record<string, React.FC<FormProps>> = {
  invoice: InvoiceForm,
  unpaidContract: UnpaidContractForm,
};

const PublicationForm: React.FC<PublicationFormProps> = (props) => {
  const { open, setOpen, documentId } = props;
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const [type, setType] = useState<PublicationType | ''>('');

  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState<string>('');

  const dispatch = useDispatch();

  const [canPublishInvoice, setCanPublishInvoice] = useState(true);
  const [canPublishUnpaidContract, setCanPublishUnpaidContract] =
    useState(true);

  const handleTypeChange = (e: SelectChangeEvent<string>) => {
    setType(e.target.value as PublicationType);
  };

  const handleSuccessClose = () => {
    setOpen(false);
    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setIdentifier('');
    setOpen(false);
  };

  const fetchPublicationStatus = async () => {
    if (!documentId) return;
    const publications = await getPublicationStatus(dispatch, documentId);
    const hasInvoicePublication = publications.some(
      (p: DocumentPublication) =>
        p.type == 'Factura' && p.unpublishedAt == null,
    );
    setCanPublishInvoice(!hasInvoicePublication);
    const hasUnpaidContractPublication = publications.some(
      (p: DocumentPublication) =>
        p.type == 'Contrato impago' && p.unpublishedAt == null,
    );
    setCanPublishUnpaidContract(!hasUnpaidContractPublication);

    if (!document) return alert('Ha ocurrido un error');
  };

  useEffect(() => {
    if (!documentId) return;
    try {
      void fetchPublicationStatus();
    } catch (error) {
      console.error(error);
    }
  }, [documentId]);

  const FormComponent = type ? formRegistry[type] : null;

  if (loading) {
    return <CenteredCircularProgress />;
  }

  return (
    <div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4"> Publicar</Typography>
            </Grid>
            <Grid item>
              <img
                src="https://pruff-public.s3.amazonaws.com/Integrations/cobranzaOnline.svg"
                alt="cobranzaOnlineLogo"
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} overflow={'hidden'}>
            <Grid item xs={12}>
              <InputLabel id="filter-label">
                Selecciona el tipo de documento
              </InputLabel>

              <Select fullWidth value={type} onChange={handleTypeChange}>
                <MenuItem value="invoice" disabled={!canPublishInvoice}>
                  Deudor (Publicar factura)
                </MenuItem>
                <MenuItem
                  value="unpaidContract"
                  disabled={!canPublishUnpaidContract}
                >
                  Cliente (Publicar contrato impago)
                </MenuItem>
              </Select>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                Ingresa la información del documento que deseas publicar.
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ my: 2 }} />
          {!loading && FormComponent && (
            <FormComponent
              documentId={documentId}
              setLoading={setLoading}
              setOpen={setOpen}
              handleSuccessClose={handleSuccessClose}
              setIdentifier={setIdentifier}
            />
          )}
          {!loading && !FormComponent && (
            <div>
              {!canPublishInvoice && (
                <Disclaimer text="Esta factura ya fue publicada (Deudor publicado)." />
              )}
              {!canPublishUnpaidContract && (
                <Disclaimer text="Este documento tiene una publicación por contrato impago (Emisor publicado)." />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <SuccessDialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        identifier={identifier}
      />
    </div>
  );
};

export default PublicationForm;
